import {
	createContext,
	FC,
	ReactNode,
	useContext,
	useEffect,
	useState,
} from 'react';
import { captureException } from '@sentry/capacitor';
import invariant from 'tiny-invariant';

import { useBusinessLayer, useUser } from '@mopla/business-logic';
import { FeedbackForBookingDto } from '@mopla/data-models';

import { getAverageRating } from './lib/getAverageRating';

interface IDriverFeedbackProviderProps {
	children: ReactNode;
}

interface IDriverFeedbackContext {
	averageRating: number;
	countOfFeedbacks: number;
	isLoading: boolean;
}

const DEFAULT_VALUES: IDriverFeedbackContext = {
	averageRating: 0,
	countOfFeedbacks: 0,
	isLoading: false,
};

const DriverFeedbackContext = createContext(DEFAULT_VALUES);

export const useDriverFeedbackContext = () => {
	const context = useContext(DriverFeedbackContext);

	invariant(
		context,
		'useDriverFeedbackContext must be used within a DriverFeedbackProvider'
	);

	return context;
};

export const DriverFeedbackProvider: FC<IDriverFeedbackProviderProps> = ({
	children,
}) => {
	const [averageRating, setAverageRating] = useState(0);
	const [countOfFeedbacks, setCountOfReviews] = useState(0);
	const [isRatingLoading, setIsRatingLoading] = useState(false);

	const { api } = useBusinessLayer();
	const { userData, isLoading } = useUser();

	useEffect(() => {
		let isMounted = true;

		if (!isLoading && userData) {
			setIsRatingLoading(true);
			api
				.get(`/api/feedback/by-driver/${userData.userId}`)
				.then((driverFeedbacks: FeedbackForBookingDto[]) => {
					if (!isMounted) return;

					if (!driverFeedbacks.length) {
						setAverageRating(0);
						setCountOfReviews(0);
						return;
					}

					const newAverageRating = getAverageRating(driverFeedbacks);

					setAverageRating(newAverageRating);
					setCountOfReviews(driverFeedbacks.length);
				})
				.catch(captureException)
				.finally(() => setIsRatingLoading(false));
		}

		return () => {
			isMounted = false;
		};
	}, [api, userData, isLoading]);

	return (
		<DriverFeedbackContext.Provider
			value={{
				averageRating,
				countOfFeedbacks,
				isLoading: isRatingLoading,
			}}
		>
			{children}
		</DriverFeedbackContext.Provider>
	);
};
